/**
 * SparklineMini
 *
 * 8-point SVG sparkline for weekly health score history.
 * Used inside the HealthPill breakdown popover.
 * Pure SVG polyline - no chart library dependency.
 *
 * Color strategy (whitelabel-safe):
 *   Pass `strokeClassName` (e.g. "text-primary-500") and the SVG uses
 *   `stroke="currentColor"` / `fill="currentColor"` so the color resolves
 *   via CSS and respects whitelabel CSS variable overrides.
 *
 *   Legacy `strokeColor` (raw hex) is kept for backwards-compat but should
 *   not be used for new call-sites - use `strokeClassName` instead.
 */

interface SparklineMiniProps {
  data: number[];
  width?: number;
  height?: number;
  /** Tailwind text-color class (e.g. "text-primary-500"). Preferred over strokeColor. */
  strokeClassName?: string;
  /** @deprecated Use strokeClassName instead. Raw hex bypasses whitelabel. */
  strokeColor?: string;
}

export const SparklineMini = ({
  data,
  width = 120,
  height = 28,
  strokeClassName = 'text-primary-500',
  strokeColor,
}: SparklineMiniProps) => {
  if (!data || data.length < 2) return null;

  const padX = 2;
  const padY = 3;
  const innerWidth = width - padX * 2;
  const innerHeight = height - padY * 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((val, i) => {
    const x = padX + (i / (data.length - 1)) * innerWidth;
    const y = padY + innerHeight - ((val - min) / range) * innerHeight;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const lastPoint = data[data.length - 1];
  const lastX = padX + innerWidth;
  const lastY = padY + innerHeight - ((lastPoint - min) / range) * innerHeight;

  // When a legacy raw hex is passed, use it directly; otherwise use currentColor
  // so the Tailwind class on the wrapper resolves the correct palette variable.
  const resolvedColor = strokeColor ?? 'currentColor';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      // Apply strokeClassName so currentColor resolves to the right palette color
      className={strokeColor ? undefined : strokeClassName}
      style={{ display: 'block' }}
    >
      {/* Area fill */}
      <polygon
        points={`${padX},${padY + innerHeight} ${points.join(' ')} ${lastX},${padY + innerHeight}`}
        fill={resolvedColor}
        fillOpacity={0.1}
      />
      {/* Line */}
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={resolvedColor}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* End dot */}
      <circle
        cx={lastX}
        cy={lastY}
        r={2}
        fill={resolvedColor}
      />
    </svg>
  );
};
